import { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { StyledSection } from '../../styles/section.styled';
import { Container } from '../../styles/container.styled';
import { StyledTitle } from '../../styles/title.styled';
import { ReactComponent as CooperationLeft } from '../../assets/images/cooperation/left.svg';
import { ReactComponent as CooperationRight } from '../../assets/images/cooperation/right.svg';
import { ReactComponent as MarqueeCheckIcon } from '../../assets/icons/marquee-check.svg';

const MARQUEE_MESSAGES = [
    'Приглашаем к сотрудничеству на особых условиях',
    'Специальные условия и тарифы для партнерских организаций',
    'Перевозки для фондов, НКО и государственных учреждений',
];

const Cooperation: FC = () => {
    const marqueeRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const track = marqueeRef.current;
        if (!track) return;

        let frameId: number;
        let lastTime = 0;
        let offset = 0;
        let contentWidth = track.scrollWidth / 2 || 1; // ширина одного цикла текста
        const duration = 25000; // 25 секунд на полный проход
        const speed = contentWidth / duration; // пикселей в миллисекунду

        const step = (timestamp: number) => {
            if (!lastTime) {
                lastTime = timestamp;
            }
            const delta = timestamp - lastTime;
            lastTime = timestamp;

            // сдвигаем трек на фиксированное расстояние пропорционально времени кадра
            offset -= speed * delta;
            if (-offset >= contentWidth) {
                offset += contentWidth;
            }

            track.style.transform = `translate3d(${offset}px, 0, 0)`;
            frameId = window.requestAnimationFrame(step);
        };

        frameId = window.requestAnimationFrame(step);

        const handleResize = () => {
            contentWidth = track.scrollWidth / 2 || 1;
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, []);

    return (
        <StyledCooperation>
            <StyledSection>
                <Container>
                    <StyledTitle>Сотрудничество</StyledTitle>
                    <StyledColumns>
                        <StyledColumn>
                            <StyledSubtitle>Благотворительные фонды</StyledSubtitle>
                            <StyledRow>
                                <StyledImageLeft />
                                <StyledTextBlock>
                                    <StyledList>
                                        <li>Благотворительный фонд AdVita</li>
                                        <li>
                                            Благотворительная организация «Перспективы»
                                        </li>
                                        <li>
                                            ГАООРДИ — Санкт-Петербургская ассоциация
                                            общественных объединений родителей детей-инвалидов
                                        </li>
                                    </StyledList>
                                </StyledTextBlock>
                            </StyledRow>
                        </StyledColumn>
                        <StyledColumn>
                            <StyledSubtitle>
                                Государственные и частные организации
                            </StyledSubtitle>
                            <StyledImageRight />
                        </StyledColumn>
                    </StyledColumns>
                </Container>
            </StyledSection>
            <MarqueeWrapper>
                <MarqueeInner ref={marqueeRef}>
                    {[...MARQUEE_MESSAGES, ...MARQUEE_MESSAGES].map((text, index) => (
                        <MarqueeItem key={index}>
                            <MarqueeCheckIcon />
                            <span>{text}</span>
                        </MarqueeItem>
                    ))}
                </MarqueeInner>
            </MarqueeWrapper>
        </StyledCooperation>
    );
};

const StyledCooperation = styled.div`
    padding-top: 80px;
    @media (max-width: 768px) {
        padding-top: 32px;
    }
`;

const StyledColumns = styled.div`
    margin-top: 48px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 29px;
    @media (max-width: 992px) {
        grid-template-columns: 1fr;
    }
`;

const StyledColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const StyledRow = styled.div`
    display: flex;
    gap: 29px;
    align-items: center;
    @media (max-width: 992px) {
        flex-direction: column;
        align-items: center;
    }
`;

const StyledImageLeft = styled(CooperationLeft)`
    flex: none;
    max-width: 253px;
    width: 100%;
`;

const StyledImageRight = styled(CooperationRight)`
    flex: none;
    max-width: 540px;
    width: 100%;
    display: block;
    margin: 0 auto;
`;

const StyledTextBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (max-width: 992px) {
        align-items: flex-start;
        text-align: left;
    }
`;

const StyledSubtitle = styled.h3`
    font-size: 20px;
    font-weight: 700;
    line-height: 120%;
    color: var(--color-text-main);
    text-align: center;
    width: 100%;
`;

const StyledList = styled.ol`
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;

    li {
        display: flex;
        align-items: baseline;
        gap: 10px;
        font-size: 16px;
        line-height: 150%;
        color: var(--color-text-main);

        &::before {
            content: '';
            flex: none;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: var(--color-primary-blue);
        }
    }

    @media (max-width: 768px) {
        align-items: flex-start;
        width: 100%;
    }
`;

const MarqueeWrapper = styled.div`
    margin: 48px auto 0;
    background: #e6f2ff;
    overflow: hidden;
    max-width: 1110px;
`;

const MarqueeInner = styled.div`
    display: flex;
    align-items: center;
    gap: 64px;
    padding: 24px 40px;
    width: max-content;
`;

const MarqueeItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
    font-size: 20px;
    font-weight: 700;
    color: var(--color-text-main);
`;

export default Cooperation;


